import { decodeToken } from '../../Services/Users.service';
import {
  CustomerAccountLite,
  getAssetSummary,
  getPlantsBySite,
  getSitesLite,
  parsePaginatedSummaryResponse,
} from '../../Services/serviceURL';
import { CustomerLite } from '../Plants/CreatePlant';

export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleString('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString;
  }
};

export const formatAttributeValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
};

export const extractUserInfoFromToken = () => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) return null;

  try {
    const decoded = decodeToken(accessToken) as Record<string, unknown>;
    return {
      email: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
      name: decoded?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
      role: decoded?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
      userType: decoded?.UserType,
      userId: decoded?.UserId,
    };
  } catch {
    return null;
  }
};

export const getIsAdminUser = (): boolean => {
  const tokenUserInfo = extractUserInfoFromToken();
  const rawRole = tokenUserInfo?.role || localStorage.getItem('userRole') || '';
  const normalizedRole = rawRole ? String(rawRole).toLowerCase().replace(/\s+/g, '') : '';
  return normalizedRole === 'admin' || normalizedRole === 'globaladmin';
};

export const mapCustomerAccountsToLite = (customerAccountsData: CustomerAccountLite[]): CustomerLite[] =>
  customerAccountsData.map((customer, index) => ({
    id: customer.id || `customer-${index}`,
    customerId: customer.customerId,
    customerName: customer.customerName,
    soldTo: customer.salesDistrictCustomer || customer.salesDistrict || '',
    sites: [],
  }));

const mapSiteRecord = (site: Record<string, unknown>) => ({
  siteId: String(site.siteId ?? site.id ?? site.SiteID ?? Math.random()),
  site: String(site.site ?? site.siteName ?? site.SiteName ?? 'Unnamed Site'),
});

export const parseSitesFromApiResponse = (response: unknown): Array<{ siteId: string; site: string }> => {
  if (Array.isArray(response)) {
    return response.map((site) => mapSiteRecord(site as Record<string, unknown>));
  }
  const r = response as Record<string, unknown>;
  const data = r?.Data ?? r?.data ?? r?.value;
  if (Array.isArray(data)) {
    return data.map((site) => mapSiteRecord(site as Record<string, unknown>));
  }
  return [];
};

const mapPlantRecord = (plant: Record<string, unknown>) => ({
  plantId: String(plant.plantId ?? plant.id ?? plant.PlantID ?? Math.random()),
  plant: String(plant.plant ?? plant.plantName ?? plant.PlantName ?? 'Unnamed Plant'),
});

export const parsePlantsFromApiResponse = (response: unknown): Array<{ plantId: string; plant: string }> => {
  if (Array.isArray(response)) {
    return response.map((plant) => mapPlantRecord(plant as Record<string, unknown>));
  }
  const r = response as { data?: { Data?: unknown[] } };
  const nested = r?.data?.Data;
  if (Array.isArray(nested)) {
    return nested.map((plant) => mapPlantRecord(plant as Record<string, unknown>));
  }
  return [];
};

export async function loadSitesForCustomer(customerId: string) {
  const response = await getSitesLite(customerId);
  return parseSitesFromApiResponse(response);
}

export async function loadPlantsForSite(siteId: string) {
  const response = await getPlantsBySite(siteId);
  return parsePlantsFromApiResponse(response);
}

export async function loadAssetsForPlant(
  customerId: string,
  siteId: string,
  plantId: string
): Promise<Array<{ id: string; assetId: string; assetName: string }>> {
  const response = await getAssetSummary(1, 500, customerId, siteId, plantId);
  const parsed = parsePaginatedSummaryResponse(response.data);
  return parsed.items.map((asset, index) => ({
    id: asset.id || asset.assetId || `asset-${index}`,
    assetId: asset.assetId || asset.id || `ASSET-${index}`,
    assetName: asset.assetName || 'Unnamed Asset',
  }));
};

const normalizeSearch = (s: unknown) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

export function filterSearchableList<T>(
  list: T[],
  search: string,
  getFields: (item: T) => Array<string | null | undefined>
): T[] {
  const term = normalizeSearch(search);
  if (!term) return list;

  return list.filter((item) => {
    const fields = getFields(item).map(normalizeSearch);
    return fields.some((field) => field === term || field.includes(term));
  });
}

const FORBIDDEN_EXTENSIONS = ['exe', 'msi', 'bat', 'cmd', 'php', 'php3', 'php4', 'phtml', 'js', 'sh', 'py', 'dll', 'jar'];

export const isUploadFileAllowed = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension) return false;
  return !FORBIDDEN_EXTENSIONS.includes(extension);
};

export const formatDocumentUploadDate = (uploadedAt: string): string => {
  const date = new Date(uploadedAt);
  const day = date.getDate();
  const getOrdinalSuffix = (n: number): string => {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}${getOrdinalSuffix(day)} ${monthNames[date.getMonth()]}, ${hours}:${minutes}`;
};
